import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { History, Undo2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { getArticleContentHistory, rollbackArticleContent } from '../lib/api/egress.js';
import type { ContentHistoryEntry } from '../lib/api/types.js';

/**
 * Append-only content-change history (mig 237): QG fix batches, generation
 * auto ai-language passes, manual edits, rollbacks, and in-place rewrites
 * from a superseding filing (mig 455). Roll back restores the
 * chosen entry's BEFORE state as a new entry — history is never mutated,
 * so a rollback is itself rollbackable.
 */
const KIND_LABEL: Record<ContentHistoryEntry['change_kind'], string> = {
  qg_fix_batch: 'QG fix batch',
  qg_fix_all: 'QG fixes (all)',
  auto_ai_language: 'Auto ai-language',
  manual_edit: 'Manual edit',
  rollback: 'Rollback',
  update_in_place: 'In-place update',
};

const KIND_CHIP: Record<ContentHistoryEntry['change_kind'], string> = {
  qg_fix_batch: 'bg-primary-100 text-primary-700',
  qg_fix_all: 'bg-primary-100 text-primary-700',
  auto_ai_language: 'bg-primary-50 text-primary-600',
  manual_edit: 'bg-grey-100 text-grey-700',
  rollback: 'bg-warning-100 text-warning-700',
  update_in_place: 'bg-info-100 text-info-700',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface ContentHistoryPanelProps {
  articleId: string;
  articleUpdatedAt: string | null;
}

export function ContentHistoryPanel({ articleId, articleUpdatedAt }: ContentHistoryPanelProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['article-content-history', articleId],
    queryFn: () => getArticleContentHistory(articleId),
    enabled: open,
  });

  const rollbackMutation = useMutation({
    mutationFn: (historyId: string) =>
      rollbackArticleContent(articleId, historyId, articleUpdatedAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      queryClient.invalidateQueries({ queryKey: ['article-content-history', articleId] });
    },
  });

  const entries = data?.entries ?? [];

  return (
    <div className="bg-surface rounded-xl border border-grey-200 shadow-md overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-6 py-3 flex items-center justify-between hover:bg-grey-50"
      >
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-grey-600" />
          <span className="text-sm font-semibold text-grey-900">Content history</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-grey-500" /> : <ChevronDown className="h-4 w-4 text-grey-500" />}
      </button>

      {open && (
        <div className="px-6 pb-4 border-t border-grey-100">
          {isLoading && (
            <p className="text-sm text-grey-500 py-3">Loading history…</p>
          )}
          {!isLoading && entries.length === 0 && (
            <p className="text-sm text-grey-500 py-3">
              No recorded content changes yet — history starts with the first
              edit, fix batch, or auto-applied pass after 30 July 2026.
            </p>
          )}
          {entries.length > 0 && (
            <ul className="divide-y divide-grey-100">
              {entries.map((e) => (
                <li key={e.history_id} className="py-2.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${KIND_CHIP[e.change_kind]}`}>
                        {KIND_LABEL[e.change_kind]}
                      </span>
                      <span className="text-xs text-grey-500">{formatDate(e.created_at)}</span>
                      <span className="text-xs text-grey-500">· {e.created_by}</span>
                      {e.applied_count > 0 && (
                        <span className="text-xs text-grey-500">· {e.applied_count} fix(es)</span>
                      )}
                      <span className="text-xs text-grey-400">
                        {e.before_len.toLocaleString()} → {e.after_len.toLocaleString()} chars
                      </span>
                    </div>
                    {e.edit_note && (
                      <p className="text-xs text-grey-600 mt-0.5 truncate" title={e.edit_note}>
                        {e.edit_note}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={rollbackMutation.isPending}
                    onClick={() => {
                      if (window.confirm(
                        'Roll the article content back to BEFORE this change?\n\n'
                        + 'The current content is preserved as a new history entry, '
                        + 'so this action is itself reversible. You will need to '
                        + 'republish + promote to update the live site.',
                      )) {
                        rollbackMutation.mutate(e.history_id);
                      }
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-grey-600 border border-grey-200 rounded-lg hover:bg-grey-50 hover:text-grey-800 disabled:opacity-50 flex-shrink-0"
                    title="Restore the content as it was before this change"
                  >
                    {rollbackMutation.isPending && rollbackMutation.variables === e.history_id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Undo2 className="h-3 w-3" />
                    )}
                    Roll back
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
